import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import API from "../lib/api";
import { cn } from "../lib/utils";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Coins,
  Cpu,
  GitBranch,
  Layers,
  RefreshCw,
  ScrollText,
  Search,
  Timer,
  X,
} from "lucide-react";
import { ConnectionsCard, ConnectionsStatCard } from "../components/connections/ConnectionsLayout";
import { PageLayout } from "../components/PageLayout";
import { CopyButton } from "../components/CopyButton";

const PAGE_SIZE = 25;
const AUTO_REFRESH_MS = 15000;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatLatency(ms) {
  if (!Number.isFinite(ms)) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function formatRelativeTime(iso) {
  const ts = new Date(iso || "").getTime();
  if (!Number.isFinite(ts)) return "—";
  const diff = Date.now() - ts;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}

function formatAbsoluteTime(iso) {
  const ts = new Date(iso || "");
  return Number.isFinite(ts.getTime()) ? ts.toLocaleString() : "—";
}

function summaryTone(status, trimmed) {
  if (status === "error") return "destructive";
  if (trimmed) return "secondary";
  return "outline";
}

function actionTone(action) {
  const a = String(action || "").toLowerCase();
  if (/(fail|error|reject|denied|unauthorized)/.test(a)) return "destructive";
  if (/(ingest|index|upload|parse|extract)/.test(a)) return "secondary";
  if (/(llm|ai_|prompt|model|evaluate|evaluation)/.test(a)) return "default";
  return "outline";
}

function severityClass(severity) {
  const s = String(severity || "").toUpperCase();
  if (s === "ERROR" || s === "CRITICAL") return "bg-red-500 text-white";
  if (s === "WARNING") return "bg-amber-500 text-white";
  if (s === "DEBUG") return "bg-muted text-muted-foreground";
  return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
}

function shortTrace(traceId) {
  return traceId ? String(traceId).slice(0, 8) : "—";
}

const SERVICE_COLORS = [
  "bg-emerald-500",
  "bg-sky-500",
  "bg-amber-500",
  "bg-violet-500",
  "bg-rose-500",
  "bg-cyan-500",
];

function serviceColor(service) {
  let hash = 0;
  for (const ch of String(service || "?")) hash = (hash * 31 + ch.charCodeAt(0)) % 997;
  return SERVICE_COLORS[hash % SERVICE_COLORS.length];
}

// ---------------------------------------------------------------------------
// Shared micro-components
// ---------------------------------------------------------------------------

/** Debounce a fast-changing value (search boxes) before hitting the API. */
function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Relative timestamp that ticks, with the absolute time in the tooltip. */
function RelativeTime({ iso, className }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 20000);
    return () => clearInterval(id);
  }, []);
  return (
    <time dateTime={iso || undefined} title={formatAbsoluteTime(iso)} className={className}>
      {formatRelativeTime(iso)}
    </time>
  );
}

/** Guided empty state: icon + title + hint instead of a blank dashed box. */
function EmptyState({ icon: Icon = Activity, title, hint, testId }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-10 text-center"
      data-testid={testId}
    >
      <div className="mb-3 rounded-full bg-muted p-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="mt-1 max-w-md text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Skeleton placeholder rows while a table loads. */
function SkeletonRows({ rows = 6 }) {
  return (
    <div className="space-y-2 p-3" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full rounded-md" />
      ))}
    </div>
  );
}

/** Prompt/completion token split as a stacked mini bar. */
function TokenBar({ prompt = 0, completion = 0, total: totalOverride }) {
  const total = Number.isFinite(totalOverride) ? totalOverride : Number(prompt || 0) + Number(completion || 0);
  if (!total) return <span className="text-[11px] text-muted-foreground">—</span>;
  const promptPct = total ? Math.round((Number(prompt || 0) / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-muted"
        title={`${prompt} prompt · ${completion} completion tokens`}
      >
        <div className="flex h-full">
          <div className="h-full bg-sky-400" style={{ width: `${promptPct}%` }} />
          <div className="h-full bg-violet-400" style={{ width: `${100 - promptPct}%` }} />
        </div>
      </div>
      <span className="text-[11px] text-muted-foreground">{total.toLocaleString()}</span>
    </div>
  );
}

/** Copyable code/prompt block used in the LLM drill-downs. */
function PromptBlock({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />
      </div>
      <pre className="max-h-56 overflow-auto rounded-md border bg-background p-3 text-[11px] whitespace-pre-wrap break-words">
        {value || "—"}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trace waterfall (depth-indented, selectable spans)
// ---------------------------------------------------------------------------

function TraceWaterfall({ trace, selectedSpanId, onSelectSpan }) {
  const spans = trace?.spans || [];
  const byId = useMemo(() => new Map(spans.map((s) => [s.span_id, s])), [spans]);

  const depthOf = useCallback(
    (span) => {
      let depth = 0;
      let current = span;
      const seen = new Set();
      while (current?.parent_span_id && byId.has(current.parent_span_id) && depth < 8) {
        if (seen.has(current.parent_span_id)) break;
        seen.add(current.parent_span_id);
        current = byId.get(current.parent_span_id);
        depth += 1;
      }
      return depth;
    },
    [byId],
  );

  const sorted = useMemo(
    () => [...spans].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [spans],
  );

  const services = useMemo(
    () => [...new Set(spans.map((s) => s.service).filter(Boolean))],
    [spans],
  );

  if (!spans.length) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="trace-empty">
        No spans recorded for this trace yet. Spans appear once instrumented
        API/worker activity has run with telemetry enabled.
      </div>
    );
  }

  const starts = spans.map((s) => new Date(s.start_time).getTime()).filter(Number.isFinite);
  const ends = spans.map((s) => new Date(s.end_time || s.start_time).getTime()).filter(Number.isFinite);
  const t0 = Math.min(...starts);
  const total = Math.max(1, Math.max(...ends) - t0);
  const selectedSpan = spans.find((s) => s.span_id === selectedSpanId);

  return (
    <div className="space-y-2" data-testid="trace-waterfall">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>
          Total {formatLatency(total)} · {spans.length} span{spans.length === 1 ? "" : "s"}
        </span>
        <div className="flex flex-wrap items-center gap-2.5">
          {services.map((svc) => (
            <span key={svc} className="inline-flex items-center gap-1">
              <span className={cn("inline-block h-2 w-2 rounded-full", serviceColor(svc))} />
              {svc}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-0.5">
        {sorted.map((span, index) => {
          const startMs = new Date(span.start_time).getTime() - t0;
          const durMs = new Date(span.end_time || span.start_time).getTime() - new Date(span.start_time).getTime();
          const width = Math.max(1.5, (durMs / total) * 100);
          const offset = (startMs / total) * 100;
          const depth = depthOf(span);
          const isError = span.status_code === "ERROR";
          const selected = selectedSpanId === span.span_id;
          const toggle = () => onSelectSpan?.(selected ? null : span.span_id);
          return (
            <div
              key={span.span_id || index}
              role="button"
              tabIndex={0}
              onClick={toggle}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle();
                }
              }}
              className={cn(
                "grid cursor-pointer grid-cols-[minmax(170px,1.2fr)_minmax(140px,2fr)_70px] items-center gap-2 rounded-md px-1 py-0.5 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
                selected && "bg-emerald-500/10 ring-1 ring-emerald-500/30",
              )}
            >
              <div className="flex min-w-0 items-center" style={{ paddingLeft: depth * 14 }}>
                {depth > 0 && <span className="mr-1 text-muted-foreground/50">└</span>}
                <span className={cn("mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full", serviceColor(span.service))} />
                <span className="truncate font-mono text-[11px]" title={span.name}>
                  {span.name}
                </span>
                {isError && <Badge variant="destructive" className="ml-1 h-4 shrink-0 px-1 text-[9px]">error</Badge>}
              </div>
              <div className="relative h-3 rounded-full bg-muted/60">
                <div
                  className={cn("absolute top-0.5 h-2 rounded-full", isError ? "bg-red-500" : serviceColor(span.service))}
                  style={{
                    left: `${Math.min(98, offset)}%`,
                    width: `${Math.max(2, Math.min(100 - Math.min(98, offset), width))}%`,
                  }}
                  title={`${span.service || "?"} · +${Math.round(startMs)}ms · ${formatLatency(span.duration_ms)}`}
                />
              </div>
              <div className={cn("text-right font-mono text-[11px]", isError ? "text-red-600" : "text-muted-foreground")}>
                {formatLatency(span.duration_ms)}
              </div>
            </div>
          );
        })}
      </div>

      {selectedSpan && (
        <div className="rounded-lg border bg-muted/20 p-3" data-testid="trace-span-inspector">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className={cn("inline-block h-2.5 w-2.5 shrink-0 rounded-full", serviceColor(selectedSpan.service))} />
              <span className="truncate font-mono text-xs font-medium" title={selectedSpan.name}>{selectedSpan.name}</span>
              {selectedSpan.status_code === "ERROR" && (
                <Badge variant="destructive" className="h-4 px-1 text-[9px]">error</Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-6 w-6 shrink-0 p-0" onClick={() => onSelectSpan?.(null)} aria-label="Close span details">
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="grid gap-x-6 gap-y-1 text-[11px] md:grid-cols-2 xl:grid-cols-3">
            <div><span className="text-muted-foreground">Service:</span> {selectedSpan.service || "—"}</div>
            <div><span className="text-muted-foreground">Duration:</span> {formatLatency(selectedSpan.duration_ms)}</div>
            <div><span className="text-muted-foreground">Status:</span> {String(selectedSpan.status_code || "—").replace("StatusCode.", "")}</div>
            <div className="truncate"><span className="text-muted-foreground">Span:</span> <span className="font-mono">{selectedSpan.span_id}</span></div>
            <div className="truncate"><span className="text-muted-foreground">Parent:</span> <span className="font-mono">{selectedSpan.parent_span_id || "—"}</span></div>
            <div><span className="text-muted-foreground">Started:</span> {formatAbsoluteTime(selectedSpan.start_time)}</div>
          </div>
          {Object.keys(selectedSpan.attributes || {}).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(selectedSpan.attributes).map(([key, value]) => (
                <Badge key={key} variant="outline" className="max-w-[260px] truncate font-mono text-[10px]" title={`${key}=${String(value)}`}>
                  {key}={String(value)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AuditLogs() {
  const { auditRun } = useAudit();
  const [tab, setTab] = useState("activity");
  const [logs, setLogs] = useState([]);
  const [activityFilter, setActivityFilter] = useState("");
  const [llmEvents, setLlmEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [llmFilters, setLlmFilters] = useState({ query: "", status: "", finding: "" });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [traces, setTraces] = useState([]);
  const [tracesTotal, setTracesTotal] = useState(0);
  const [tracesLoading, setTracesLoading] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceDetailTab, setTraceDetailTab] = useState("spans");
  const [selectedSpanId, setSelectedSpanId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const runId = auditRun?.id || "";
  const location = useLocation();
  const locationSearch = location.search;
  const debouncedLlmQuery = useDebouncedValue(llmFilters.query, 300);
  const debouncedActivityFilter = useDebouncedValue(activityFilter, 250);

  const touch = useCallback(() => setLastUpdated(Date.now()), []);

  const fetchLogs = useCallback(async () => {
    setActivityLoading(true);
    try {
      const params = auditRun?.id ? { run_id: auditRun.id } : {};
      const { data } = await API.get("/audit-logs", { params: { ...params, limit: 200 } });
      // Backend returns {logs:[...], total, ...}; older mocks/versions
      // returned a bare array — accept both.
      setLogs(Array.isArray(data) ? data : data?.logs || data?.items || []);
    } catch (e) {
      setLogs([]);
    } finally {
      setActivityLoading(false);
      touch();
    }
  }, [auditRun?.id, touch]);

  const fetchLlmStats = useCallback(async () => {
    if (!runId) {
      setStats(null);
      return;
    }
    try {
      const { data } = await API.get(`/audit-runs/${runId}/llm-events/stats`);
      setStats(data || null);
    } catch (e) {
      setStats(null);
    }
  }, [runId]);

  const fetchLlmEvents = useCallback(async () => {
    if (!runId) {
      setLlmEvents([]);
      setTotal(0);
      return;
    }
    setLlmLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (llmFilters.status) params.status = llmFilters.status;
      if (llmFilters.finding) params.finding_id = llmFilters.finding;
      const query = debouncedLlmQuery.trim();
      if (query) {
        // 32-char hex -> trace id, anything else -> session id (both are
        // supported server-side filters).
        if (/^[0-9a-f]{32}$/i.test(query)) params.trace_id = query;
        else params.session_id = query;
      }
      const { data } = await API.get(`/audit-runs/${runId}/llm-events`, { params });
      setLlmEvents(data?.events || data?.logs || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setLlmEvents([]);
      setTotal(0);
    } finally {
      setLlmLoading(false);
      touch();
    }
  }, [debouncedLlmQuery, llmFilters.status, llmFilters.finding, page, runId, touch]);

  const fetchTraces = useCallback(async () => {
    if (!runId) {
      setTraces([]);
      setTracesTotal(0);
      return;
    }
    setTracesLoading(true);
    try {
      const { data } = await API.get(`/audit-runs/${runId}/traces`, { params: { limit: 100 } });
      setTraces(data?.traces || []);
      setTracesTotal(data?.total || 0);
    } catch (e) {
      setTraces([]);
      setTracesTotal(0);
    } finally {
      setTracesLoading(false);
      touch();
    }
  }, [runId, touch]);

  const openTrace = useCallback(async (traceId, options = {}) => {
    if (!traceId) return;
    const { silent = false } = options;
    if (!silent) {
      setTraceLoading(true);
      setTraceDetailTab("spans");
      setSelectedSpanId(null);
    }
    try {
      const { data } = await API.get(`/traces/${traceId}`);
      setSelectedTrace(data);
      setSelectedTraceId(traceId);
    } catch (e) {
      setSelectedTrace({ trace_id: traceId, spans: [], logs: [], audit_events: [], llm_events: [], stats: {} });
      setSelectedTraceId(traceId);
    } finally {
      if (!silent) setTraceLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchLogs();
    fetchLlmStats();
    fetchLlmEvents();
    fetchTraces();
    if (selectedTraceId) openTrace(selectedTraceId, { silent: true });
  }, [fetchLogs, fetchLlmStats, fetchLlmEvents, fetchTraces, selectedTraceId, openTrace]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchLlmStats();
  }, [fetchLlmStats]);

  useEffect(() => {
    fetchLlmEvents();
  }, [fetchLlmEvents]);

  useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  // Deep links from the finding decision-basis panel (Phase 8):
  //   /logs?trace=<32-hex>   -> Traces tab with that trace already open
  //   /logs?llmFinding=<id>  -> LLM Events tab filtered to that finding
  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    const trace = (params.get("trace") || "").trim().toLowerCase();
    const finding = (params.get("llmFinding") || "").trim();
    if (trace && /^[0-9a-f]{32}$/.test(trace)) {
      setTab("traces");
      openTrace(trace);
    }
    if (finding) {
      setTab("llm");
      setLlmFilters((prev) => (prev.finding === finding ? prev : { ...prev, finding }));
    }
  }, [locationSearch, openTrace]);

  useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [runId, debouncedLlmQuery, llmFilters.status, llmFilters.finding]);

  // Live mode: re-poll everything (and the open trace) while enabled.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(refreshAll, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, refreshAll]);

  const filteredLogs = useMemo(() => {
    const base = Array.isArray(logs) ? logs : [];
    const lowered = debouncedActivityFilter.trim().toLowerCase();
    const matches = (log) => {
      if (!lowered) return true;
      const action = String(log.action || "").toLowerCase();
      const details = JSON.stringify(log.details || {}).toLowerCase();
      const trace = String(log.trace_id || "").toLowerCase();
      return action.includes(lowered) || details.includes(lowered) || trace.includes(lowered);
    };
    return base
      .filter(matches)
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [debouncedActivityFilter, logs]);

  const groupedLogs = useMemo(() => {
    const groups = [];
    const bucket = new Map();
    for (const log of filteredLogs) {
      const groupName = log.audit_run_name || "Unknown Audit";
      if (!bucket.has(groupName)) {
        const value = { name: groupName, logs: [] };
        bucket.set(groupName, value);
        groups.push(value);
      }
      bucket.get(groupName).logs.push(log);
    }
    return groups;
  }, [filteredLogs]);

  // Stats endpoint returns flat fields; keep nested fallback for older shapes.
  const statSource = stats?.stats || stats || {};
  const statCards = [
    { label: "LLM calls", value: statSource.total_calls ?? "—", icon: Cpu },
    { label: "Errors", value: statSource.errors ?? statSource.total_errors ?? "—", icon: AlertTriangle, tone: "red" },
    { label: "Avg latency", value: Number.isFinite(statSource.avg_latency_ms) ? formatLatency(Math.round(statSource.avg_latency_ms)) : "—", icon: Timer },
    { label: "Total tokens", value: Number.isFinite(statSource.total_tokens) ? statSource.total_tokens.toLocaleString() : "—", icon: Coins },
  ];

  const tabs = [
    { id: "activity", label: "Activity", icon: Activity, testId: "activity-tab-btn" },
    { id: "traces", label: "Traces", icon: GitBranch, testId: "traces-tab-btn", count: runId ? tracesTotal : null },
    { id: "llm", label: "LLM Events", icon: Cpu, testId: "llm-tab-btn", count: runId ? total : null },
  ];

  const traceStats = selectedTrace?.stats || {};
  const traceSpans = selectedTrace?.spans || [];
  const traceSubTabs = [
    { id: "spans", label: "Spans", count: traceSpans.length },
    { id: "logs", label: "Logs", count: (selectedTrace?.logs || []).length },
    { id: "activity", label: "Activity", count: (selectedTrace?.audit_events || []).length },
    { id: "llm", label: "LLM", count: (selectedTrace?.llm_events || []).length },
  ];

  const llmPageStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const llmPageEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <PageLayout
      icon={Activity}
      title="Audit Logs & Telemetry"
      subtitle={auditRun ? `System & LLM logs for: ${auditRun.name}` : "All audit activity logs"}
      actions={
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1 rounded-xl border bg-muted/40 p-1" role="tablist" aria-label="Log sections">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  data-testid={t.testId}
                  className={cn(
                    "flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50",
                    active
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                  {t.count > 0 && (
                    <span
                      className={cn(
                        "ml-0.5 rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums",
                        active ? "bg-white/20 text-white" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {lastUpdated && (
            <span className="hidden text-[11px] text-muted-foreground lg:inline" title={formatAbsoluteTime(new Date(lastUpdated).toISOString())}>
              Updated <RelativeTime iso={new Date(lastUpdated).toISOString()} />
            </span>
          )}

          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground select-none">
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              aria-label="Auto refresh every 15 seconds"
              data-testid="auto-refresh-switch"
            />
            Live
          </label>

          <Button
            variant="ghost"
            size="sm"
            onClick={refreshAll}
            data-testid="refresh-logs-btn"
            aria-label="Refresh"
            className="h-8 w-8 cursor-pointer rounded-lg p-0 transition-colors hover:bg-muted"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", autoRefresh && "animate-spin [animation-duration:3s]")} />
          </Button>
        </div>
      }
      data-testid="audit-logs-page"
    >
      {tab === "traces" && (
        <>
          {!runId ? (
            <EmptyState
              icon={GitBranch}
              testId="traces-select-run-empty"
              title="Select an audit run to see its traces"
              hint="Pick an audit run from the sidebar or header — every user action and worker chain recorded for it shows up here with spans, logs and LLM calls."
            />
          ) : (
            <div className="grid gap-3 lg:grid-cols-[minmax(320px,2fr)_minmax(360px,3fr)]">
              <ConnectionsCard
                title={`Traces (${tracesTotal})`}
                subtitle="One trace per user action or worker chain, joined across services. Newest first."
                contentClassName="p-0"
              >
                {tracesLoading && traces.length === 0 ? (
                  <SkeletonRows rows={8} />
                ) : traces.length === 0 ? (
                  <EmptyState
                    icon={GitBranch}
                    testId="traces-empty"
                    title="No traces recorded for this run yet"
                    hint="Traces appear as soon as instrumented API or worker activity runs for this audit run."
                  />
                ) : (
                  <Table className="table-dense">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operation &amp; timing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {traces.map((trace) => {
                        const selected = selectedTraceId === trace.trace_id;
                        return (
                          <TableRow
                            key={trace.trace_id}
                            onClick={() => openTrace(trace.trace_id)}
                            data-testid="trace-row"
                            className={cn(
                              "cursor-pointer transition-colors",
                              selected && "bg-emerald-500/10 hover:bg-emerald-500/10",
                            )}
                          >
                            <TableCell className="py-2.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium" title={trace.root_name || ""}>
                                    {trace.root_name || "—"}
                                  </p>
                                  <p className="font-mono text-[10px] text-muted-foreground">{shortTrace(trace.trace_id)}</p>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-xs font-semibold tabular-nums">{formatLatency(trace.duration_ms)}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    <RelativeTime iso={trace.start_time} />
                                  </p>
                                </div>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                                <span>{trace.span_count} span{trace.span_count === 1 ? "" : "s"}</span>
                                {trace.llm_calls > 0 && <span className="font-medium text-violet-600 dark:text-violet-400">{trace.llm_calls} LLM</span>}
                                {trace.has_errors ? (
                                  <Badge variant="destructive" className="h-4 px-1.5 text-[9px]">
                                    {trace.error_count} error{trace.error_count === 1 ? "" : "s"}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="h-4 px-1.5 text-[9px] text-emerald-600">ok</Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </ConnectionsCard>

              <ConnectionsCard
                title={selectedTrace ? `Trace ${shortTrace(selectedTrace.trace_id)}` : "Trace detail"}
                subtitle={selectedTrace ? "Span waterfall and everything logged under this trace." : "Select a trace to inspect its timeline."}
                actions={selectedTrace ? (
                  <div className="flex items-center gap-1.5">
                    <CopyButton
                      value={selectedTrace.trace_id}
                      label="Copy full trace id"
                      showLabel
                      data-testid="copy-trace-id"
                      className="h-8 px-2 font-mono text-[11px]"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 cursor-pointer rounded-lg p-0"
                      onClick={() => { setSelectedTrace(null); setSelectedTraceId(null); setSelectedSpanId(null); }}
                      aria-label="Close trace detail"
                      data-testid="close-trace-detail"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}
                contentClassName="space-y-3"
              >
                {!selectedTrace ? (
                  <EmptyState
                    icon={Layers}
                    testId="trace-detail-empty"
                    title="Click a trace to inspect it"
                    hint="The waterfall shows every span (API → worker → LLM) in order; logs, audit events and LLM calls are one click away."
                  />
                ) : traceLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-6 w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="trace-stats">
                      <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3" />{traceStats.span_count ?? 0} spans</span>
                      <span className="inline-flex items-center gap-1"><ScrollText className="h-3 w-3" />{traceStats.log_count ?? 0} logs</span>
                      <span className="inline-flex items-center gap-1"><Cpu className="h-3 w-3" />{traceStats.llm_call_count ?? 0} LLM calls</span>
                      <span className="inline-flex items-center gap-1"><Coins className="h-3 w-3" />{traceStats.llm_tokens ?? 0} tokens</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /><RelativeTime iso={selectedTrace.spans?.[0]?.start_time} /></span>
                      {traceStats.has_errors && (
                        <Badge variant="destructive" className="h-4 px-1.5 text-[10px]">errors</Badge>
                      )}
                    </div>

                    <TraceWaterfall trace={selectedTrace} selectedSpanId={selectedSpanId} onSelectSpan={setSelectedSpanId} />

                    <div className="flex gap-1.5 pt-1" role="tablist" aria-label="Trace contents">
                      {traceSubTabs.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          role="tab"
                          aria-selected={traceDetailTab === sub.id}
                          onClick={() => setTraceDetailTab(sub.id)}
                          className={cn(
                            "flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
                            traceDetailTab === sub.id
                              ? "border border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          {sub.label}
                          <span className="rounded-full bg-muted px-1.5 text-[9px] font-bold tabular-nums text-muted-foreground">{sub.count}</span>
                        </button>
                      ))}
                    </div>

                    {traceDetailTab === "logs" && (
                      <div className="max-h-72 space-y-1 overflow-auto" data-testid="trace-logs">
                        {(selectedTrace.logs || []).length === 0 && (
                          <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                            No log records under this trace.
                          </div>
                        )}
                        {(selectedTrace.logs || []).map((log, index) => (
                          <div key={index} className="flex items-start gap-2 rounded border bg-muted/20 px-2 py-1 font-mono text-[11px]">
                            <span className={cn("shrink-0 rounded px-1 font-bold", severityClass(log.severity))}>
                              {log.severity}
                            </span>
                            <span className="shrink-0 text-muted-foreground">{log.logger}</span>
                            <span className="min-w-0 flex-1 break-words">{log.message}</span>
                            <span className="shrink-0 text-muted-foreground"><RelativeTime iso={log.timestamp} /></span>
                          </div>
                        ))}
                      </div>
                    )}

                    {traceDetailTab === "activity" && (
                      <div className="max-h-72 space-y-1 overflow-auto" data-testid="trace-activity">
                        {(selectedTrace.audit_events || []).length === 0 && (
                          <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                            No audit events under this trace.
                          </div>
                        )}
                        {(selectedTrace.audit_events || []).map((event) => (
                          <div key={event.id} className="flex flex-wrap items-center gap-1.5 rounded border bg-muted/20 px-2 py-1 text-[11px]">
                            <Badge variant={actionTone(event.action)} className="font-mono text-[10px]">{event.action}</Badge>
                            <span className="text-muted-foreground">{event.actor}</span>
                            <span className="ml-auto font-mono text-muted-foreground">
                              <RelativeTime iso={event.timestamp} />
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {traceDetailTab === "llm" && (
                      <div className="max-h-72 space-y-1 overflow-auto" data-testid="trace-llm">
                        {(selectedTrace.llm_events || []).length === 0 && (
                          <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
                            No LLM calls under this trace.
                          </div>
                        )}
                        {(selectedTrace.llm_events || []).map((event) => (
                          <div key={event.id} className="rounded border bg-muted/20 px-2 py-1.5 text-[11px]">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Badge variant={summaryTone(event.status, event.prompt_trimmed)} className="h-4 px-1.5 font-mono text-[9px]">
                                {event.status === "error" ? "error" : "ok"}
                              </Badge>
                              <span className="font-medium">{event.provider}/{event.model_version || "?"}</span>
                              <TokenBar prompt={event.prompt_tokens} completion={event.completion_tokens} total={event.total_tokens} />
                              <span className="text-muted-foreground">{formatLatency(event.latency_ms)}</span>
                              {event.control_id && <Badge variant="outline" className="h-4 px-1.5 text-[9px]">{event.control_id}</Badge>}
                              {event.prompt_version && (
                                <span className="ml-auto inline-flex items-center gap-0.5 font-mono text-[9px] text-muted-foreground">
                                  {event.prompt_version}
                                  <CopyButton value={event.prompt_version} label="Copy prompt version" className="h-5 px-1" />
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </ConnectionsCard>
            </div>
          )}
        </>
      )}

      {tab === "llm" && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            {statCards.map((card) => (
              <ConnectionsStatCard key={card.label} value={card.value} label={card.label} icon={card.icon} tone={card.tone || "neutral"} />
            ))}
          </div>

          <ConnectionsCard
            title="LLM Event Explorer"
            subtitle={auditRun ? "Structured prompt and response records for the active audit run." : "Select an audit run to inspect LLM events."}
            actions={(
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={llmFilters.query}
                  onChange={(e) => setLlmFilters((prev) => ({ ...prev, query: e.target.value }))}
                  placeholder="Session or trace id…"
                  className="h-9 w-full text-sm sm:w-52"
                  aria-label="Filter by session or trace id"
                  data-testid="llm-search-input"
                />
                <select
                  value={llmFilters.status}
                  onChange={(e) => setLlmFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="h-9 cursor-pointer rounded-md border bg-background px-3 text-sm transition-colors hover:border-foreground/30"
                  aria-label="Filter by status"
                  data-testid="llm-status-filter"
                >
                  <option value="">All statuses</option>
                  <option value="success">success</option>
                  <option value="error">error</option>
                </select>
                {llmFilters.finding && (
                  <button
                    type="button"
                    onClick={() => setLlmFilters((prev) => ({ ...prev, finding: "" }))}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-emerald-600/40 bg-emerald-500/10 px-2.5 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 dark:text-emerald-300"
                    title={`Showing agent calls for finding ${llmFilters.finding} — click to clear`}
                    aria-label="Clear finding filter"
                    data-testid="llm-finding-filter-chip"
                  >
                    Finding {llmFilters.finding.slice(0, 8)}…
                    <X className="h-3 w-3" />
                  </button>
                )}
                {(llmFilters.query || llmFilters.status || llmFilters.finding) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 cursor-pointer rounded-md px-2 text-xs"
                    onClick={() => setLlmFilters({ query: "", status: "", finding: "" })}
                    aria-label="Clear filters"
                  >
                    <X className="mr-1 h-3 w-3" /> Clear
                  </Button>
                )}
              </div>
            )}
            contentClassName="space-y-3"
          >
              {!runId ? (
                <EmptyState
                  icon={Cpu}
                  testId="llm-select-run-empty"
                  title="Select an audit run to inspect LLM events."
                  hint="Every model call made for a run — prompts, outputs, token usage and prompt versions — is listed here."
                />
              ) : llmLoading && llmEvents.length === 0 ? (
                <SkeletonRows rows={5} />
              ) : llmEvents.length === 0 ? (
                <EmptyState
                  icon={Cpu}
                  title="No LLM events found"
                  hint={llmFilters.query || llmFilters.status || llmFilters.finding ? "Try clearing the current filters." : "LLM events appear once an AI-assisted evaluation has run for this audit run."}
                />
              ) : (
                <div className="space-y-2">
                  {llmEvents.map((event) => {
                    const expanded = expandedId === event.id;
                    return (
                      <div key={event.id} className="overflow-hidden rounded-lg border transition-shadow hover:shadow-sm" data-testid="llm-event-row">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : event.id)}
                          aria-expanded={expanded}
                          className="grid w-full cursor-pointer grid-cols-[24px_1fr] items-start gap-x-3 gap-y-1.5 bg-card px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/40 md:grid-cols-[24px_76px_1fr_150px_92px_150px]"
                        >
                          <span className="pt-0.5 text-muted-foreground">
                            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                          <Badge variant={summaryTone(event.status, event.prompt_trimmed)} className="w-fit font-mono text-[11px]">
                            {event.status === "error" ? "error" : event.prompt_trimmed ? "trimmed" : "ok"}
                          </Badge>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{event.provider || "unknown"}</span>
                              <Badge variant="outline" className="text-[10px]">{event.audit_run_name || auditRun?.name || "Global"}</Badge>
                              <span className="text-xs text-muted-foreground">{event.actor || "system"}</span>
                              {event.trace_id && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openTrace(event.trace_id); setTab("traces"); }}
                                  className="cursor-pointer truncate font-mono text-xs text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                                  title={`Open trace ${event.trace_id}`}
                                  data-testid="llm-event-trace-link"
                                >
                                  {shortTrace(event.trace_id)}
                                </button>
                              )}
                              <span className="truncate font-mono text-xs text-muted-foreground">{event.session_id || "—"}</span>
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {event.model_version || "unknown model"}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <RelativeTime iso={event.timestamp} />
                          </div>
                          <div className="text-xs font-medium tabular-nums">{formatLatency(event.latency_ms)}</div>
                          <TokenBar prompt={event.prompt_tokens} completion={event.completion_tokens} total={event.total_tokens} />
                        </button>
                        {expanded && (
                          <div className="space-y-4 border-t bg-muted/20 px-4 py-4" data-testid="llm-event-detail">
                            {event.error && (
                              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span className="min-w-0 break-words">{event.error}</span>
                              </div>
                            )}
                            <div className="grid gap-2 text-sm md:grid-cols-2 xl:grid-cols-4">
                              <div><span className="text-muted-foreground">Model:</span> {event.model_version || "—"}</div>
                              <div><span className="text-muted-foreground">Temperature:</span> {event.temperature ?? "—"}</div>
                              <div><span className="text-muted-foreground">Prompt tokens:</span> {event.prompt_tokens ?? 0}</div>
                              <div><span className="text-muted-foreground">Completion tokens:</span> {event.completion_tokens ?? 0}</div>
                              <div className="md:col-span-2">
                                <span className="text-muted-foreground">Prompt version:</span>{" "}
                                <span className="inline-flex items-center gap-1 font-mono text-xs">
                                  {event.prompt_version || "—"}
                                  {event.prompt_version && <CopyButton value={event.prompt_version} label="Copy prompt version" />}
                                </span>
                              </div>
                              <div><span className="text-muted-foreground">Control:</span> {event.control_id || "—"}</div>
                              <div><span className="text-muted-foreground">Finding:</span> {event.finding_id || "—"}</div>
                              <div className="md:col-span-2"><span className="text-muted-foreground">Session:</span> <span className="font-mono text-xs">{event.session_id || "—"}</span></div>
                              <div className="md:col-span-2"><span className="text-muted-foreground">Trace:</span> <span className="font-mono text-xs">{shortTrace(event.trace_id)}</span></div>
                            </div>
                            <PromptBlock label="System prompt" value={event.system_prompt} />
                            <PromptBlock label="User prompt" value={event.user_prompt} />
                            <PromptBlock label="Model output" value={event.model_output} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {runId && total > PAGE_SIZE && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    data-testid="llm-prev-page"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    Showing {llmPageStart}–{llmPageEnd} of {total.toLocaleString()}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * PAGE_SIZE >= total}
                    onClick={() => setPage((current) => current + 1)}
                    data-testid="llm-next-page"
                  >
                    Next
                  </Button>
                </div>
              )}
          </ConnectionsCard>
        </>
      )}

      {tab === "activity" && (
        <ConnectionsCard
          title={`Activity Timeline${filteredLogs.length ? ` (${filteredLogs.length})` : ""}`}
          subtitle={auditRun ? "Immutable audit trail for the active run. Click a row for details, or a trace id to open the full timeline." : "Recent audit activity across accessible runs."}
          actions={(
            <div className="relative w-full md:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                placeholder="Filter activity or trace…"
                className="h-9 w-full pl-8 pr-8 text-sm"
                aria-label="Filter activity"
                data-testid="log-filter-input"
              />
              {activityFilter && (
                <button
                  type="button"
                  onClick={() => setActivityFilter("")}
                  aria-label="Clear filter"
                  className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          contentClassName="p-0"
        >
            {activityLoading && filteredLogs.length === 0 ? (
              <SkeletonRows rows={7} />
            ) : filteredLogs.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={Activity}
                  title="No logs found"
                  hint={activityFilter ? "Nothing matches the current filter — try a shorter term or a trace id." : "Activity appears here as soon as users or workers act on this audit run."}
                />
              </div>
            ) : auditRun ? (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]" />
                    <TableHead className="w-[150px]">Time</TableHead>
                    <TableHead className="w-[180px]">Action</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="w-[100px]">Actor</TableHead>
                    <TableHead className="w-[110px]">Trace</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => {
                    const expanded = expandedLogId === log.id;
                    const detailsJson = JSON.stringify(log.details || {}, null, expanded ? 2 : 0);
                    return (
                      <Fragment key={log.id}>
                        <TableRow
                          className="cursor-pointer transition-colors hover:bg-muted/40"
                          onClick={() => setExpandedLogId(expanded ? null : log.id)}
                          data-testid="activity-row"
                        >
                          <TableCell className="pr-0 text-muted-foreground">
                            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            <RelativeTime iso={log.timestamp} />
                          </TableCell>
                          <TableCell>
                            <Badge variant={actionTone(log.action)} className="font-mono text-xs">{log.action}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[420px] truncate font-mono text-xs">
                            {detailsJson.replace(/\s+/g, " ")}
                          </TableCell>
                          <TableCell className="text-xs">{log.actor || log.user || "system"}</TableCell>
                          <TableCell>
                            {log.trace_id ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openTrace(log.trace_id); setTab("traces"); }}
                                className="cursor-pointer font-mono text-xs text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                                title={`Open trace ${log.trace_id}`}
                                data-testid="activity-trace-link"
                              >
                                <Search className="mr-1 inline h-3 w-3" />
                                {shortTrace(log.trace_id)}
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-muted/20 p-0">
                              <div className="space-y-2 px-4 py-3">
                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                  <Badge variant="outline" className="font-mono text-[10px]">{log.action}</Badge>
                                  {log.event_type && <Badge variant="secondary" className="text-[10px]">{log.event_type}</Badge>}
                                  <span className="text-muted-foreground">{formatAbsoluteTime(log.timestamp)}</span>
                                  {log.trace_id && <span className="font-mono text-[10px] text-muted-foreground">trace {shortTrace(log.trace_id)}</span>}
                                  <CopyButton value={detailsJson} label="Copy details" className="ml-auto" />
                                </div>
                                <pre className="max-h-64 overflow-auto rounded-md border bg-background p-3 text-[11px] whitespace-pre-wrap break-words">
                                  {detailsJson}
                                </pre>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="space-y-6 p-4">
                {groupedLogs.map((group) => (
                  <div key={group.name} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={group.name === "Global" ? "secondary" : "outline"}>{group.name}</Badge>
                      <span className="text-xs text-muted-foreground">{group.logs.length} event{group.logs.length === 1 ? "" : "s"}</span>
                    </div>
                    <Table className="table-dense">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[150px]">Time</TableHead>
                          <TableHead className="w-[180px]">Action</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead className="w-[100px]">Actor</TableHead>
                          <TableHead className="w-[110px]">Trace</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.logs.map((log) => (
                          <TableRow key={log.id} className="transition-colors hover:bg-muted/40">
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              <RelativeTime iso={log.timestamp} />
                            </TableCell>
                            <TableCell>
                              <Badge variant={actionTone(log.action)} className="font-mono text-xs">{log.action}</Badge>
                            </TableCell>
                            <TableCell className="max-w-[420px] truncate font-mono text-xs">
                              {JSON.stringify(log.details || {}).replace(/\s+/g, " ")}
                            </TableCell>
                            <TableCell className="text-xs">{log.actor || log.user || "system"}</TableCell>
                            <TableCell>
                              {log.trace_id ? (
                                <button
                                  type="button"
                                  onClick={() => { openTrace(log.trace_id); setTab("traces"); }}
                                  className="cursor-pointer font-mono text-xs text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400"
                                  title={`Open trace ${log.trace_id}`}
                                  data-testid="activity-trace-link"
                                >
                                  {shortTrace(log.trace_id)}
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            )}
        </ConnectionsCard>
      )}
    </PageLayout>
  );
}
